/**
 * Correlation ID utility functions for frontend.
 *
 * Generates and validates correlation IDs for request tracking.
 */

import { v4 as uuidv4 } from 'uuid';
import type { CorrelationId } from '../types/correlation-id';

/**
 * Generate a new correlation ID.
 *
 * @returns A new UUID v4 correlation ID
 */
export function generateCorrelationId(): CorrelationId {
  return uuidv4();
}

/**
 * Validate that a string is a valid correlation ID (UUID v4 format).
 *
 * @param id - String to validate
 * @returns True if valid UUID v4 format
 */
export function isValidCorrelationId(id: string): boolean {
  if (!id || typeof id !== 'string') {
    return false;
  }

  // UUID v4 format: xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

  return uuidRegex.test(id);
}

/**
 * Extract correlation ID from response headers.
 *
 * @param response - Fetch API Response object
 * @returns Correlation ID if found in headers
 */
export function extractCorrelationIdFromResponse(response: Response): CorrelationId | null {
  const correlationId = response.headers.get('x-correlation-id') || response.headers.get('x-request-id');

  if (correlationId && isValidCorrelationId(correlationId)) {
    return correlationId;
  }

  return null;
}
